import { useState, type ChangeEvent, type FormEvent } from "react";
import { Icon } from "../../components/Icon";
import {
  Button,
  Card,
  Pill,
  SectionHeading,
} from "../../components/ui";
import { parseEmailRoster } from "../../lib/csv";
import { usePortal } from "../../state/PortalContext";

export function FacultyCohortsPage() {
  const { cohort, students, addRoster, updateRiskRules } = usePortal();
  const [csv, setCsv] = useState("");
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [rules, setRules] = useState(cohort.riskRules);
  const [busy, setBusy] = useState(false);

  const importRoster = async () => {
    const entries = parseEmailRoster(csv);
    if (entries.length === 0) return;
    setBusy(true);
    try {
      await addRoster(entries);
      setCsv("");
    } finally {
      setBusy(false);
    }
  };

  const inviteOne = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      await addRoster([{ email, fullName }]);
      setEmail("");
      setFullName("");
    } finally {
      setBusy(false);
    }
  };

  const readFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setCsv(String(reader.result));
    reader.readAsText(file);
  };

  return (
    <div className="space-y-7">
      <SectionHeading
        eyebrow="Cohorts & Rosters"
        title={cohort.name}
        detail={`${cohort.term} · ${cohort.startsAt} to ${cohort.endsAt}. Invitations are passwordless and scoped to this cohort.`}
        action={<Pill tone="cyan">{students.length} students</Pill>}
      />

      <div className="grid gap-6 xl:grid-cols-2">
        <Card className="p-7">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-retro text-[8px] tracking-widest text-cyan-300">
                BULK ENROLLMENT
              </p>
              <h2 className="mt-2 font-display text-2xl font-bold text-white">
                Import a roster CSV
              </h2>
            </div>
            <label className="cursor-pointer rounded-xl border border-slate-700 bg-slate-900 p-3 text-slate-300 hover:border-slate-500">
              <Icon name="Upload" size={20} />
              <span className="sr-only">Choose CSV file</span>
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={readFile}
                className="hidden"
              />
            </label>
          </div>
          <p className="mt-3 text-sm leading-6 text-slate-500">
            Accepted columns: <code>email,fullName</code>. A header row is
            optional; invalid and duplicate emails are ignored.
          </p>
          <textarea
            rows={9}
            value={csv}
            onChange={(event) => setCsv(event.target.value)}
            placeholder={"email,fullName\nstudent@example.edu,Jamie Player"}
            className="mt-5 w-full rounded-2xl border border-slate-700 bg-slate-950 p-4 font-mono text-xs leading-6 text-white placeholder:text-slate-700"
          />
          <div className="mt-4 flex items-center justify-between">
            <span className="text-xs text-slate-500">
              {parseEmailRoster(csv).length} valid row(s)
            </span>
            <Button
              onClick={() => void importRoster()}
              disabled={busy || parseEmailRoster(csv).length === 0}
            >
              <Icon name="UserPlus" size={17} />
              Prepare invitations
            </Button>
          </div>
        </Card>

        <Card className="p-7">
          <p className="font-retro text-[8px] tracking-widest text-violet-300">
            INDIVIDUAL INVITE
          </p>
          <h2 className="mt-2 font-display text-2xl font-bold text-white">
            Add one adventurer
          </h2>
          <form onSubmit={inviteOne} className="mt-6 space-y-4">
            <label className="block text-xs font-bold text-slate-300">
              Full name
              <input
                value={fullName}
                onChange={(event) => setFullName(event.target.value)}
                className="mt-2 min-h-12 w-full rounded-xl border border-slate-700 bg-slate-950 px-4 text-sm text-white"
                placeholder="Jamie Player"
              />
            </label>
            <label className="block text-xs font-bold text-slate-300">
              University email
              <input
                type="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className="mt-2 min-h-12 w-full rounded-xl border border-slate-700 bg-slate-950 px-4 text-sm text-white"
                placeholder="student@example.edu"
              />
            </label>
            <Button className="w-full" disabled={busy}>
              <Icon name="Mail" size={17} />
              Invite to cohort
            </Button>
          </form>

          <div className="mt-8 border-t border-slate-800 pt-6">
            <h3 className="text-sm font-extrabold text-white">Current roster</h3>
            <div className="scrollbar-thin mt-4 max-h-64 space-y-2 overflow-y-auto pr-2">
              {students.map((student) => (
                <div
                  key={student.id}
                  className="flex items-center gap-3 rounded-xl border border-slate-800 bg-slate-950/45 p-3"
                >
                  <span className="grid h-9 w-9 place-items-center rounded-xl bg-indigo-400/10 text-indigo-300">
                    <Icon name="User" size={17} />
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-xs font-bold text-slate-200">
                      {student.fullName}
                    </p>
                    <p className="truncate text-[10px] text-slate-600">
                      {student.email}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      </div>

      <Card className="p-7">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="font-retro text-[8px] tracking-widest text-rose-300">
              EARLY SUPPORT SIGNALS
            </p>
            <h2 className="mt-2 font-display text-2xl font-bold text-white">
              Configure transparent at-risk rules
            </h2>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-500">
              A student is flagged when any rule triggers. Faculty always see
              the specific reasons rather than an unexplained score.
            </p>
          </div>
          <Button onClick={() => updateRiskRules(rules)}>
            <Icon name="Save" size={17} />
            Save rules
          </Button>
        </div>
        <div className="mt-7 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <RuleField
            label="Inactivity threshold"
            suffix="days"
            value={rules.inactivityDays}
            onChange={(inactivityDays) => setRules({ ...rules, inactivityDays })}
          />
          <RuleField
            label="Expected progress"
            suffix="% of pace"
            value={rules.expectedProgressRatio * 100}
            onChange={(value) =>
              setRules({ ...rules, expectedProgressRatio: value / 100 })
            }
          />
          <RuleField
            label="Recent failed attempts"
            suffix="attempts"
            value={rules.recentFailureCount}
            onChange={(recentFailureCount) =>
              setRules({ ...rules, recentFailureCount })
            }
          />
          <RuleField
            label="Core mastery overdue"
            suffix="days"
            value={rules.coreCompetencyOverdueDays}
            onChange={(coreCompetencyOverdueDays) =>
              setRules({ ...rules, coreCompetencyOverdueDays })
            }
          />
        </div>
      </Card>
    </div>
  );
}

function RuleField({
  label,
  suffix,
  value,
  onChange,
}: {
  label: string;
  suffix: string;
  value: number;
  onChange(value: number): void;
}) {
  return (
    <label className="rounded-2xl border border-slate-800 bg-slate-950/45 p-4">
      <span className="text-xs font-bold text-slate-300">{label}</span>
      <div className="mt-3 flex items-center gap-2">
        <input
          type="number"
          min={0}
          value={value}
          onChange={(event) => onChange(Number(event.target.value))}
          className="min-h-11 min-w-0 flex-1 rounded-xl border border-slate-700 bg-slate-950 px-3 text-sm text-white"
        />
        <span className="text-[10px] text-slate-600">{suffix}</span>
      </div>
    </label>
  );
}
